import React, { useContext } from 'react';
import styled from 'styled-components';

import { Attachment, Emoji, Separator } from './shared/editor_action_buttons';
import { EditorContainer, useEditor } from './shared/editor';
import CharacterCount from 'components/text_editor/character_count';
import Composer, { Header, HeaderText, useComposer } from './shared/composer';
import ComposerContext from './contexts/composer_context';
import ConversationMessage, { MessageChannelType } from 'models/conversation_message';
import ConversationMessageAddressField from 'components/composer/conversation_message_address_field';
import MessagingEditor from './editors/messaging';
import PlainTextSerializer from 'components/text_editor/serializers/plaintext_serializer';
import SendComposerConversationMessage from 'actions/composer/send_composer_conversation_message';

export function ConversationMessageComposer() {
  const { composition } = useContext(ComposerContext);
  const composer = useComposer(SendComposerConversationMessage);
  const editorProps = useEditor('bodyHtml', composer);

  const [composerProps] = composer;
  const {
    customerAddress,
    customerDisplayName,
    companyAddress,
    companyDisplayName,
    channel,
    channelDisplayTitle,
  } = getPropertiesFromComposition(composition);

  return (
    <Composer
      submitText="Send"
      {...composerProps}
      disableSubmit={isOverCharacterLimit()}
      includeAttachments={includeAttachments(channel)}
    >
      <Header>
        <Left>
          <HeaderText color="green400">{channelDisplayTitle || channel}</HeaderText>
          <div className="conversationMessageComposer-address-label">To</div>
          <ConversationMessageAddressField address={customerDisplayName || customerAddress} />
        </Left>
        <Right>
          <React.Fragment>
            <Emoji {...editorProps} />
            {renderAttachmentButton()}
            <Separator />
          </React.Fragment>
          <div className="conversationMessageComposer-address-label">From</div>
          <div className="conversationMessageComposer-from-container">
            <ConversationMessageAddressField address={companyDisplayName || companyAddress} />
          </div>
        </Right>
      </Header>
      <EditorContainer includeAttachments>
        <MessagingEditor placeholder="Type to reply" {...editorProps} />
        {renderCharacterCount()}
      </EditorContainer>
    </Composer>
  );

  function isOverCharacterLimit() {
    let characterCount = (editorProps.value ? PlainTextSerializer.serialize(editorProps.value) : '').length;
    switch (channel) {
      case MessageChannelType.INSTAGRAM_DIRECT:
        return characterCount > ConversationMessage.MAX_INSTAGRAM_MESSAGE_SIZE;
      case MessageChannelType.TWITTER:
      case MessageChannelType.WHATSAPP:
        return characterCount > ConversationMessage.MAX_CONVERSATION_MESSAGE_SIZE;
      default:
        return false;
    }
  }

  function renderCharacterCount() {
    if (!isOverCharacterLimit(editorProps.value, channel) && channel !== MessageChannelType.INSTAGRAM_DIRECT) {
      return null;
    }
    let maxCharacters;
    if (channel === MessageChannelType.INSTAGRAM_DIRECT) {
      maxCharacters = ConversationMessage.MAX_INSTAGRAM_MESSAGE_SIZE;
    } else {
      maxCharacters = ConversationMessage.MAX_CONVERSATION_MESSAGE_SIZE;
    }
    return <CharacterCount {...editorProps} maxCharacters={maxCharacters} />;
  }

  function renderAttachmentButton() {
    switch (channel) {
      case MessageChannelType.INSTAGRAM_DIRECT:
      case MessageChannelType.TWITTER:
        return <Attachment allowedFileTypes={'image/jpeg, image/png, image/gif'} alwaysShow />;
      case MessageChannelType.WHATSAPP:
        return <Attachment allowedFileTypes={'image/jpeg, image/png'} alwaysShow />;
      default:
        return null;
    }
  }
}

function includeAttachments(channel) {
  switch (channel) {
    case MessageChannelType.INSTAGRAM_DIRECT:
    case MessageChannelType.TWITTER:
    case MessageChannelType.WHATSAPP:
      return true;
    default:
      return false;
  }
}

export default React.memo(ConversationMessageComposer);

function getPropertiesFromComposition(composition) {
  return {
    customerAddress: composition.content.customerAddress,
    customerDisplayName: composition.content.customerDisplayName,
    companyAddress: composition.content.companyAddress,
    companyDisplayName: composition.content.companyDisplayName,
    channel: composition.content.channel,
    channelDisplayTitle: composition.content.channelDisplayTitle,
  };
}

const Left = styled.div`
  align-items: center;
  display: flex;
  flex-grow: 1;
`;
const Right = styled.div`
  align-items: center;
  display: flex;
`;
